/**
 * Date and timestamp formatting utilities for audit logs.
 */

import { isEmptyString } from '@/utils/string';

/**
 * Type guard to check if a value is an ISO 8601 timestamp string.
 *
 * @param val - The value to check
 * @returns true if the value is a string matching ISO 8601 format
 *
 * @example
 * isISOTimestamp('2025-11-27T00:41:08Z') // true
 * isISOTimestamp('2025-11-27T00:41:08.123+05:30') // true
 * isISOTimestamp('not a timestamp') // false
 */
export const isISOTimestamp = (val: unknown): val is string => {
  if (typeof val !== 'string') return false;
  // Match ISO 8601 format: 2025-11-27T00:41:08Z or similar variations
  const isoPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
  return isoPattern.test(val);
};

/**
 * Format a timestamp (ISO string or Unix) to a human-readable display format.
 *
 * Handles:
 * - ISO 8601 strings
 * - Unix timestamps in seconds (10 digits)
 * - Unix timestamps in milliseconds (13 digits)
 *
 * @param val - The timestamp value (string or number)
 * @returns Formatted date string like "Nov 27, 2025, 12:41:08 AM"
 *
 * @example
 * formatTimestamp('2025-11-27T00:41:08Z') // "Nov 27, 2025, 12:41:08 AM"
 * formatTimestamp(1732668068) // "Nov 27, 2025, 12:41:08 AM"
 * formatTimestamp(1732668068000) // "Nov 27, 2025, 12:41:08 AM"
 */
export const formatTimestamp = (val: string | number): string => {
  let date: Date;

  if (typeof val === 'number') {
    // Unix timestamps in seconds (10 digits) or milliseconds (13 digits)
    if (val > 1000000000 && val < 10000000000) {
      date = new Date(val * 1000);
    } else if (val > 1000000000000 && val < 10000000000000) {
      date = new Date(val);
    } else {
      return String(val);
    }
  } else {
    date = new Date(val);
  }

  if (Number.isNaN(date.getTime())) return String(val);

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

// Shared helper for formatting dates in human-friendly format
interface DateFormatParts {
  datePrefix: string;
  timeStr: string;
  timezone: string;
}

const formatDateParts = (date: Date, timezoneOverride?: string): DateFormatParts => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Check if it's today
  const isToday = dateOnly.getTime() === today.getTime();

  // Check if it's yesterday
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = dateOnly.getTime() === yesterday.getTime();

  // Format time with seconds (e.g., "9:40:32 pm")
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  const displayHours = hours % 12 !== 0 ? hours % 12 : 12;
  const timeStr = `${displayHours}:${minutes}:${seconds} ${ampm}`;

  // Get timezone abbreviation
  const timezone = timezoneOverride ?? date.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop() ?? '';

  // Format date prefix
  let datePrefix: string;
  if (isToday) {
    datePrefix = 'Today at';
  } else if (isYesterday) {
    datePrefix = 'Yesterday at';
  } else {
    datePrefix =
      date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
      }) + ' at';
  }

  return { datePrefix, timeStr, timezone };
};

/**
 * Format ISO date string to human-friendly format.
 *
 * @param isoDateString - ISO 8601 date string
 * @returns Human-friendly date string like "Today at 9:40:32 pm (PST)"
 */
export const formatHumanFriendlyDateFromISO = (isoDateString: string): string => {
  try {
    const date = new Date(isoDateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const { datePrefix, timeStr, timezone } = formatDateParts(date);
    return `${datePrefix} ${timeStr} (${timezone})`;
  } catch {
    return '';
  }
};

/**
 * Format Date object to human-friendly format.
 *
 * @param date - Date object to format
 * @param timezoneOverride - Optional timezone abbreviation override
 * @returns Human-friendly date string like "Today at 9:40:32 pm (PST)"
 */
export const formatHumanFriendlyDate = (date: Date, timezoneOverride: string | undefined): string => {
  try {
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const { datePrefix, timeStr, timezone } = formatDateParts(date, timezoneOverride);
    return `${datePrefix} ${timeStr} (${timezone})`;
  } catch {
    return '';
  }
};

/**
 * Parse and format duration strings (e.g., "168h0m0s" -> "1 week")
 *
 * @param value - Duration string in Go duration format
 * @returns Human-readable duration string
 */
export const formatDuration = (value: string): string => {
  // Match patterns like "168h0m0s", "24h30m15s", "30m", "5s", "1w2d3h4m5s", etc.
  let hours = 0;
  let minutes = 0;
  let seconds = 0;
  let days = 0;
  let weeks = 0;

  // Match weeks (e.g., "1w")
  const weekMatch = value.match(/(\d+)w/);
  if (weekMatch != null) weeks = Number.parseInt(weekMatch[1], 10);

  // Match days (e.g., "2d")
  const dayMatch = value.match(/(\d+)d/);
  if (dayMatch != null) days = Number.parseInt(dayMatch[1], 10);

  // Match hours (e.g., "168h")
  const hourMatch = value.match(/(\d+)h/);
  if (hourMatch != null) hours = Number.parseInt(hourMatch[1], 10);

  // Match minutes (e.g., "30m")
  const minuteMatch = value.match(/(\d+)m/);
  if (minuteMatch != null) minutes = Number.parseInt(minuteMatch[1], 10);

  // Match seconds (e.g., "15s")
  const secondMatch = value.match(/(\d+)s/);
  if (secondMatch != null) seconds = Number.parseInt(secondMatch[1], 10);

  // If no matches found, return as-is
  if (weekMatch == null && dayMatch == null && hourMatch == null && minuteMatch == null && secondMatch == null) {
    return value;
  }

  // Convert everything to seconds for calculation
  const totalSeconds = weeks * 7 * 24 * 60 * 60 + days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60 + seconds;

  if (totalSeconds === 0) return '0 seconds';

  // Convert to appropriate units
  const weeksCount = Math.floor(totalSeconds / (7 * 24 * 60 * 60));
  const daysCount = Math.floor((totalSeconds % (7 * 24 * 60 * 60)) / (24 * 60 * 60));
  const hoursCount = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutesCount = Math.floor((totalSeconds % (60 * 60)) / 60);
  const secondsCount = totalSeconds % 60;

  const parts: string[] = [];
  if (weeksCount > 0) parts.push(`${weeksCount} ${weeksCount === 1 ? 'week' : 'weeks'}`);
  if (daysCount > 0) parts.push(`${daysCount} ${daysCount === 1 ? 'day' : 'days'}`);
  if (hoursCount > 0) parts.push(`${hoursCount} ${hoursCount === 1 ? 'hour' : 'hours'}`);
  if (minutesCount > 0) parts.push(`${minutesCount} ${minutesCount === 1 ? 'minute' : 'minutes'}`);
  if (secondsCount > 0) parts.push(`${secondsCount} ${secondsCount === 1 ? 'second' : 'seconds'}`);

  const result = parts.join(', ');
  return !isEmptyString(result) ? result : '0 seconds';
};

/**
 * Check if a string is a duration format (e.g., "168h0m0s")
 */
export const isDurationString = (value: string): boolean => {
  // Check for patterns like "168h0m0s", "24h30m15s", "30m", "5s", "1w2d3h4m5s", etc.
  return /^(\d+[dhmsw])+$/.test(value);
};

/**
 * Format a number of seconds to a human-readable duration string.
 * Uses the single most appropriate unit for clarity (e.g., "8 days" not "1 week 1 day").
 *
 * @param seconds - The number of seconds
 * @returns Human-readable duration string like "8 days" or "2.5 hours"
 *
 * @example
 * formatSecondsToHumanReadable(691200) // "8 days"
 * formatSecondsToHumanReadable(9000) // "2 hours 30 minutes"
 * formatSecondsToHumanReadable(3600) // "1 hour"
 * formatSecondsToHumanReadable(90) // "1 minute 30 seconds"
 */
export const formatSecondsToHumanReadable = (seconds: number): string => {
  if (seconds === 0) return '0 seconds';
  if (seconds < 0) return `${formatSecondsToHumanReadable(Math.abs(seconds))} (negative)`;

  const MINUTE = 60;
  const HOUR = MINUTE * 60;
  const DAY = HOUR * 24;

  // Helper to pluralize
  const pluralize = (count: number, singular: string, plural: string): string =>
    count === 1 ? `${count} ${singular}` : `${count} ${plural}`;

  // Use the largest unit that fits cleanly, prefer days over weeks for clarity
  // (e.g., "8 days" is clearer than "1 week 1 day")

  // If it's at least 1 day, use days as the primary unit
  if (seconds >= DAY) {
    const totalDays = Math.floor(seconds / DAY);
    const remainingHours = Math.floor((seconds % DAY) / HOUR);

    if (remainingHours === 0) {
      return pluralize(totalDays, 'day', 'days');
    }
    // If there's a remainder, show days + hours
    return `${pluralize(totalDays, 'day', 'days')} ${pluralize(remainingHours, 'hour', 'hours')}`;
  }

  // If it's at least 1 hour, use hours as the primary unit
  if (seconds >= HOUR) {
    const totalHours = Math.floor(seconds / HOUR);
    const remainingMinutes = Math.floor((seconds % HOUR) / MINUTE);

    if (remainingMinutes === 0) {
      return pluralize(totalHours, 'hour', 'hours');
    }
    return `${pluralize(totalHours, 'hour', 'hours')} ${pluralize(remainingMinutes, 'minute', 'minutes')}`;
  }

  // If it's at least 1 minute, use minutes as the primary unit
  if (seconds >= MINUTE) {
    const totalMinutes = Math.floor(seconds / MINUTE);
    const remainingSeconds = seconds % MINUTE;

    if (remainingSeconds === 0) {
      return pluralize(totalMinutes, 'minute', 'minutes');
    }
    return `${pluralize(totalMinutes, 'minute', 'minutes')} ${pluralize(remainingSeconds, 'second', 'seconds')}`;
  }

  // Less than a minute, use seconds
  return pluralize(seconds, 'second', 'seconds');
};
